/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Tooltip from "@mui/material/Tooltip";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiAvatar from "components/VuiAvatar";
import { Card, CardActionArea, Chip, Grid, Icon, Skeleton, styled } from "@mui/material";
import { IoCube } from "react-icons/io5";
import {
  FaExternalLinkAlt,
} from "react-icons/fa";
import typography from "assets/theme/base/typography";
import linearGradient from "assets/theme/functions/linearGradient";
import cardContent from "assets/theme/components/card/cardContent";
import VuiProgress from "components/VuiProgress";
import { useEffect, useState } from "react";
import { getChainIcon } from "utilities/utilites";
import { formatCurrency } from "utilities/utilites";
import { shortenAddress } from "utilities/utilites";
import { formatTimeDifference } from "utilities/utilites";
import { getChainName } from "utilities/utilites";
import contractABI from "../../../../assets/contractABI.json";
import { calculateTVL } from "utilities/utilites";
import { useReadContract } from "wagmi";
import { ethers } from "ethers";
import { TelegramShare, TwitterShare, WhatsappShare } from "react-share-kit";

const StatusIndicator = styled(VuiBox)(({ status }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  backgroundColor:
    status === 0  ? "#10B981" : status === 1 ? "#F59E0B" : status === 2 ? "#e31a1a" : "#0f1535",
  boxShadow: status === 0 ? "0 0 8px rgba(16, 185, 129, 0.6)" : "none",
  animation: status === 0 ? "pulse 2s infinite" : "none",
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)" },
    "70%": { boxShadow: "0 0 0 6px rgba(16, 185, 129, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)" },
  },
}));

function MarketCard({
  marketId,
  marketTitle,
  marketCreationDate,
  motherToken,
  motherPairAddress,
  motherChain,
  motherTVL,
  childToken,
  childPairAddress,
  childChain,
  childTVL,
  action,
  status,
  creator,
  motherTokenDecimal,
  childTokenDecimal,
  mergedAt
}) {
  const [loading, setLoading] = useState(false);

  const [motherMarket, setMotherMarket] = useState(null);
  const [childMarket, setChildMarket] = useState(null);
  const [priceImpact, setPriceImpact] = useState(0);
  const [expanded, setExpanded] = useState(false);
  

  const {
    data: marketTVL,
    isLoading,
    isError,
  } = useReadContract({
    abi: contractABI,
    address: process.env.REACT_APP_PROXY_CONTRACT_ADDRESS,
    functionName: "getTotalDepositsByMarket",
    args: [marketId],
  });
  

  // const fetchOpenOceanPriceImpact = async (childTVL,childPriceUsd,childliquidity,motherliquidity) => {
  //   try {
  //     const response = await fetch(
  //       `https://open-api.openocean.finance/v3/${chain}/quote?gasPrice=3&inTokenAddress=${fromToken}&outTokenAddress=${toToken}&amount=${amount}&slippage=1&account=0x929B44e589AC4dD99c0282614e9a844Ea9483aaa`,
  //       {
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     // const data = await response.json();

  //     // 
  //     // 
  //     // return priceImpact;
  //     // console.log("fethc price")
  //     // var impact = 0
  //     // console.log(ethers.utils.formatEther(childTVL))
  //     // console.log(childPriceUsd)
  //     // console.log(motherliquidity)
  //     // var CTT = (ethers.utils.formatEther(childTVL)*childPriceUsd)
  //     // console.log(CTT)
  //     // console.log(childliquidity)

  //     // console.log(motherliquidity/2)
  //     // if((childliquidity/2) >= CTT){
  //     //   console.log("liqudity from child is there")
  //     //   impact =  (CTT*100) / (motherliquidity/2)
  //     // }else{
  //     //   console.log("liqudity from child is not there")
  //     //   impact = 0
  //     // }
  //     setPriceImpact(parseFloat(impact).toFixed(2) ?? "0");
  //   } catch (error) {
  //     console.error("Error fetching price impact:", error);
  //   }
  // };

  const fetchLiFiRoute = async (requestBody) => {
    
    const lifiApiUrl = "https://li.quest/v1/quote"; // Li.Fi API endpoint
    const queryParams = new URLSearchParams(requestBody);
    
    try {
      const response = await fetch(`${lifiApiUrl}?${queryParams.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch route");

      const data = await response.json();

      // Extract values
      const fromAmountUSD = parseFloat(data.estimate.fromAmountUSD);
      const toAmountUSD = parseFloat(data.estimate.toAmountUSD);

      // Calculate price impact
      const priceImpact = ((fromAmountUSD - toAmountUSD) / fromAmountUSD) * 100;

      return {
        fromAmountUSD,
        toAmountUSD,
        priceImpact: priceImpact.toFixed(2),
      };
    } catch (error) {
      console.error("Error fetching route:", error);
      return null;
    }
  };

  useEffect(() => {
    if (!motherChain || !motherPairAddress || !childChain || !childPairAddress) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Call Li.Fi API for price impact
        await fetchLiFiRoute({
          fromChain: childChain.toString(),
          fromToken: childToken,
          fromAmount: childTVL.toString(),
          toChain: motherChain.toString(),
          toToken: motherToken,
          fromAddress: process.env.REACT_APP_PROXY_CONTRACT_ADDRESS,
          toAddress: process.env.REACT_APP_PROXY_CONTRACT_ADDRESS,
        }).then((route) => {
          
          setPriceImpact(route ? route.priceImpact : "N/a");
        });

        // Fetch Dex Screener market data
        const [motherMarketRes, childMarketRes] = await Promise.all([
          fetch(
            `https://api.dexscreener.com/latest/dex/pairs/${getChainName(
              motherChain
            )}/${motherPairAddress}`
          )
            .then((res) => res.json())
            .catch(() => null),

          fetch(
            `https://api.dexscreener.com/latest/dex/pairs/${getChainName(
              childChain
            )}/${childPairAddress}`
          )
            .then((res) => res.json())
            .catch(() => null),
        ]);

        setMotherMarket(motherMarketRes?.pair || null);
        setChildMarket(childMarketRes?.pair || null);
      } catch (error) {
        console.error("Error fetching market data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [motherChain, motherPairAddress, childChain, childPairAddress]);

  // useEffect(() => {
  //   if (!motherChain || !motherPairAddress || !childChain || !childPairAddress) return;
  //   // fetchOpenOceanPriceImpact(childTVL, childPriceUsd, childliquidity,motherliquidity);

  //   console.log(fetchLiFiRoute({
  //     fromChainId: childChain,
  //     fromTokenAddress: childToken, // ETH on Base
  //     fromAmount: childTVL, // 10 ETH
  //     toChainId: motherChain,
  //     toTokenAddress: motherToken, // USDC on Base
  //     fromAddress: process.env.REACT_APP_PROXY_CONTRACT_ADDRESS,
  //     toAddress: process.env.REACT_APP_PROXY_CONTRACT_ADDRESS
  //   }))

  //   setLoading(true);

  //   // Fetch data for mother and child chains
  //   Promise.all([
  //     fetch(
  //       `https://api.dexscreener.com/latest/dex/pairs/${getChainName(
  //         motherChain
  //       )}/${motherPairAddress}`
  //     )
  //       .then((res) => res.json())
  //       .then((data) => setMotherMarket(data.pair))
  //       .catch(() => setMotherMarket(null)),

  //     fetch(
  //       `https://api.dexscreener.com/latest/dex/pairs/${getChainName(
  //         childChain
  //       )}/${childPairAddress}`
  //     )
  //       .then((res) => res.json())
  //       .then((data) => setChildMarket(data.pair))
  //       .catch(() => setChildMarket(null)),
  //   ]).finally(() => setLoading(false));
  // }, [motherChain, motherPairAddress, childChain, childPairAddress]);

  // Example usage: fetchPriceImpact(childMarket.TVL);

  if (isLoading || loading) {
    return (
      <Card sx={{ borderRadius: "16px", overflow: "hidden", background: "rgba(26, 32, 53, 0.7)" }}>
        <VuiBox p={3}>
          <Skeleton
            variant="text"
            width="60%"
            height={36}
            sx={{ mb: 1, bgcolor: "rgba(255,255,255,0.1)" }}
          />
          <Skeleton
            variant="text"
            width="40%"
            height={20}
            sx={{ mb: 2, bgcolor: "rgba(255,255,255,0.1)" }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={120}
            sx={{ mb: 2, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.1)" }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={120}
            sx={{ borderRadius: "12px", bgcolor: "rgba(255,255,255,0.1)" }}
          />
        </VuiBox>
      </Card>
    );
  }

  if (!motherMarket && !childMarket) {
    return (
      <Card sx={{ borderRadius: "16px", overflow: "hidden", background: "rgba(26, 32, 53, 0.7)" }}>
        <VuiBox p={3} textAlign="center">
          <VuiTypography variant="h6" color="white">
            Failed to load market data
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  
  const StatusTag = ({ status }) => {
    const getStatusColor = () => {
      switch(status) {
        case 0: return "#10B981";  // Green for Live
        case 1: return "#F59E0B";  // Yellow for Processing
        case 2: return "#e31a1a";  // Red for Closed
        default: return "#0f1535"; // Dark blue for Failed
      }
    };
  
    return (
      <VuiBox
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          transform: 'translate(25%, -25%) rotate(45deg)',
          backgroundColor: getStatusColor(),
          padding: '8px 24px',  // Increased padding
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          minWidth: '80px',  // Added minimum width to give it more substance
          height: '20px',    // Fixed height to simulate text space
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    );
  };

  // Card wrapper component
  const CardWrapper = ({ children, status, statusColor }) => (
    <Card
      sx={{
        position: 'relative',
        borderRadius: "16px",
        overflow: "hidden",
        background: "rgba(26, 32, 53, 0.7)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 15px 30px rgba(0,0,0,0.3)",
        },
      }}
    >
      {status && <StatusTag status={status} />}
      <CardActionArea>{children}</CardActionArea>
    </Card>
  );
  // const CardWrapper = ({ children }) => (
  //   <Card
  //     sx={{
  //       borderRadius: "16px",
  //       overflow: "hidden",
  //       background: "rgba(26, 32, 53, 0.7)",
  //       backdropFilter: "blur(10px)",
  //       boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
  //       transition: "transform 0.3s, box-shadow 0.3s",
  //       "&:hover": {
  //         transform: "translateY(-4px)",
  //         boxShadow: "0 15px 30px rgba(0,0,0,0.3)",
  //       },
  //     }}
  //   >
  //     <CardActionArea>{children}</CardActionArea>
  //   </Card>
  // );

  return (
    <CardWrapper status={status}>
      <VuiBox p={3}>
        <VuiBox
          sx={({ breakpoints }) => ({
            [breakpoints.only("xl")]: {
              minHeight: "200px",
            },
          })}
        >
          <VuiBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={3}
          >
            {/* Left side - Title and creation date and share icons */}
            <VuiBox width="100%" maxWidth={{ sm: "calc(100% - 120px)" }}>
              <VuiBox display="flex" alignItems="center" mb={0.5}>
                {/* Fixed size status indicator */}
                {/* <VuiBox width="12px" height="12px" borderRadius="50%"  flexShrink={0}>
                  <StatusIndicator status={status} mr={1.5} />
                </VuiBox> */}
                <VuiTypography
                  variant="h5"
                  color="white"
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {marketTitle}
                </VuiTypography>
              </VuiBox>
              <VuiTypography
                variant="caption"
                color="text"
                fontWeight="regular"
              
                sx={{ display: "block" }}
              >
              {mergedAt ? `Merged ${formatTimeDifference(Number(mergedAt))}`: `Created ${formatTimeDifference(Number(marketCreationDate))}`}
              </VuiTypography>
              <VuiBox display="flex" alignItems="center" mt={1}  gap={1}>
                <TwitterShare
                  url={`https://merge.markets/marketdata/${marketId}`}
                  size={22}
                  round={true}
                  title="@Merge.markets merge market"
                  hashtags={["merge.markets", "mergemarkets"]}
                />
                <TelegramShare
                  url={`https://merge.markets/marketdata/${marketId}`}
                  size={22}
                  round={true}
                />
                {/* <WhatsappShare url={`https://merge-fun.vercel.app/marketdata/${marketId}`} size={22}  round={true} title="@Merge.markets merge market" separator=":: "/> */}
              </VuiBox>
            </VuiBox>

            {/* Price impact badge - Floating position on smaller screens */}
            <VuiBox
              px={2}
              py={1}
              mt={{ xs: 1, sm: 0 }}
              borderRadius="full"
              sx={{
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(5px)",
                alignSelf: { xs: "flex-start", sm: "center" },
                flexShrink: 0,
              }}
            >
              <VuiTypography variant="button" color="white" fontWeight="bold">
                <VuiTypography variant="button" color="text" component="span" mr={1}>
                  Impact:
                </VuiTypography>
                {priceImpact == "N/a" ? priceImpact : `~ ${priceImpact}%`}
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          <VuiBox
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p="16px"
            mb="15px"
            mt="15px"
            sx={{
              background: `#2766a8a8`,
              minHeight: "110px",
              borderRadius: "16px",
            }}
          >
            {/* Token Label */}
            <VuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              mb={2}
            >
              {/* Left Side: Icon + Pair Name */}
              <VuiBox display="flex" alignItems="center">
                <VuiBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "3px",
                  }}
                >
                  {getChainIcon(motherMarket?.chainId)}
                  <img
                    src={`https://dd.dexscreener.com/ds-data/dexes/${motherMarket?.dexId}.png`}
                    width={20}
                    height={20}
                    alt={motherMarket?.dexId}
                  />
                </VuiBox>

                <VuiBox
                  ml={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="32px"
                  height="32px"
                  borderRadius="50%"
                  bgcolor="white"
                  mr={1}
                >
                  {motherMarket?.info?.imageUrl ? (
                    <VuiBox
                      component="img"
                      src={motherMarket?.info?.imageUrl}
                      borderRadius="10px"
                      width="50px"
                      height="50px"
                    />
                  ) : (
                    <VuiTypography variant="body2" color="white">
                      ?
                    </VuiTypography> // Show '?' if no image
                  )}
                </VuiBox>

                <VuiTypography variant="caption" fontWeight="bold" color="white" ml="5px">
                  {motherMarket?.baseToken?.symbol
                    ? `${motherMarket?.baseToken?.symbol} / ${motherMarket?.quoteToken?.symbol}`
                    : "N/A"}

                  <a
                    href={motherMarket?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: "5px", color: "inherit" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaExternalLinkAlt />
                  </a>
                </VuiTypography>
              </VuiBox>

              {/* Right Side: Mother Token */}
              <VuiTypography variant="caption" fontWeight="bold" color="text">
                Mother Token
              </VuiTypography>
            </VuiBox>

            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <VuiTypography variant="button" color="text">
                Liquidity
              </VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold" color="white">
                ~{formatCurrency(motherMarket?.liquidity?.usd)}
              </VuiTypography>
            </VuiBox>

            {/* Token Address */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiTypography variant="button" color="text">
                Token
              </VuiTypography>
              <VuiBox
                display="flex"
                alignItems="center"
                px={2}
                py={1}
                mb={1}
                borderRadius="8px"
                sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)", cursor: "pointer" }}
                onClick={(e) => {
                  navigator.clipboard.writeText(motherMarket?.baseToken?.address);
                  e.stopPropagation();
                }}
              >
                <Icon sx={{ color: "white", fontSize: 18, mr: 1 }}>content_copy</Icon>
                <VuiTypography variant="caption" fontWeight="bold" color="white">
                  {shortenAddress(motherMarket?.baseToken?.address)}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

  

            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiTypography variant="button" color="text">
                Locked(TVL):
              </VuiTypography>
              <VuiBox display="flex" flexDirection="column" gap={1}>
                <VuiProgress
                  value={calculateTVL(motherTVL, marketTVL[0])}
                  label
                  color="success"
                  sx={{ background: "#2D2E5F", width: "150px" }}
                />
              </VuiBox>
            </VuiBox>

            <VuiBox display="flex" justifyContent="space-between" sx={{ marginTop: "8px" }}>
              {/* Target column */}
              <VuiBox>
                <VuiTypography variant="caption" color="text" fontSize="xs">
                  Target: {parseFloat(ethers.utils.formatUnits(motherTVL,motherTokenDecimal === 0n ? 18 : motherTokenDecimal)).toFixed(2)}
                </VuiTypography>
                <VuiTypography variant="caption" color="text" fontSize="xs" display="block">
                  (${" "}
                  {(
                    parseFloat(ethers.utils.formatUnits(motherTVL,motherTokenDecimal === 0n ? 18 : motherTokenDecimal)) * motherMarket?.priceUsd || 0
                  ).toFixed(2)}
                  )
                </VuiTypography>
              </VuiBox>

              {/* Locked column */}
              <VuiBox>
                <VuiTypography variant="caption" color="text" fontSize="xs">
                  Locked: {parseFloat(ethers.utils.formatUnits(marketTVL[0],motherTokenDecimal === 0n ? 18 : motherTokenDecimal)).toFixed(2)}
                </VuiTypography>
                <VuiTypography variant="caption" color="text" fontSize="xs" display="block">
                  (${" "}
                  {(
                    parseFloat(ethers.utils.formatUnits(marketTVL[0],motherTokenDecimal === 0n ? 18 : motherTokenDecimal)) * motherMarket?.priceUsd || 0
                  ).toFixed(2)}
                  )
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>

          <VuiBox
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p="16px"
            mb="20px"
            sx={{
              background: `#163d6687`,
              minHeight: "110px",
              borderRadius: "16px",
            }}
          >
            {/* Token Label */}
            <VuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              mb={2}
            >
              {/* Left Side: Icon + Pair Name */}
              <VuiBox display="flex" alignItems="center">
                <VuiBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "3px",
                  }}
                >
                  {getChainIcon(childMarket?.chainId)}
                  <img
                    src={`https://dd.dexscreener.com/ds-data/dexes/${childMarket?.dexId}.png`}
                    width={20}
                    height={20}
                    alt={childMarket?.dexId}
                  />
                </VuiBox>
                <VuiBox
                  ml={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="32px"
                  height="32px"
                  borderRadius="50%"
                  bgcolor="white"
                  mr={1}
                >
                  {childMarket?.info?.imageUrl ? (
                    <VuiBox
                      component="img"
                      src={childMarket?.info?.imageUrl}
                      borderRadius="10px"
                      width="50px"
                      height="50px"
                    />
                  ) : (
                    <VuiTypography variant="body2" color="white">
                      ?
                    </VuiTypography> // Show '?' if no image
                  )}
                </VuiBox>
                <VuiTypography variant="caption" fontWeight="bold" color="white" ml="5px">
                  {childMarket?.baseToken?.symbol
                    ? `${childMarket?.baseToken?.symbol} / ${childMarket?.quoteToken?.symbol}`
                    : "N/A"}

                  <a
                    href={childMarket?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: "5px", color: "inherit" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaExternalLinkAlt />
                  </a>
                </VuiTypography>
              </VuiBox>

              {/* Right Side: Child Token */}
              <VuiTypography variant="caption" fontWeight="bold" color="text">
                Child Token
              </VuiTypography>
            </VuiBox>

            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <VuiTypography variant="button" color="text">
                Liquidity
              </VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold" color="white">
                ~{formatCurrency(childMarket?.liquidity?.usd)}
              </VuiTypography>
            </VuiBox>

            {/* Token Address */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiTypography variant="button" color="text">
                Token
              </VuiTypography>
              <VuiBox
                display="flex"
                alignItems="center"
                px={2}
                py={1}
                mb={1}
                borderRadius="8px"
                sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)", cursor: "pointer" }}
                onClick={(e) => {
                  navigator.clipboard.writeText(childMarket?.baseToken?.address);
                  e.stopPropagation();
                }}
              >
                <Icon sx={{ color: "white", fontSize: 18, mr: 1 }}>content_copy</Icon>
                <VuiTypography variant="caption" fontWeight="bold" color="white">
                  {shortenAddress(childMarket?.baseToken?.address)}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiTypography variant="button" color="text">
                Locked(TVL):
              </VuiTypography>
              <VuiBox display="flex" flexDirection="column" gap={1}>
                <VuiProgress
                  value={calculateTVL(childTVL, marketTVL[1])}
                  label
                  color="success"
                  sx={{ background: "#2D2E5F", width: "150px" }}
                />
              </VuiBox>
            </VuiBox>
            <VuiBox display="flex" justifyContent="space-between" sx={{ marginTop: "8px" }}>
              {/* Target column */}
              <VuiBox>
                <VuiTypography variant="caption" color="text" fontSize="xs">
                  Target: {parseFloat(ethers.utils.formatUnits(childTVL,childTokenDecimal === 0n ? 18 : childTokenDecimal)).toFixed(2)}
                </VuiTypography>
                <VuiTypography variant="caption" color="text" fontSize="xs" display="block">
                  (${" "}
                  {(
                    parseFloat(ethers.utils.formatUnits(childTVL,childTokenDecimal === 0n ? 18 : childTokenDecimal)) * childMarket?.priceUsd || 0
                  ).toFixed(2)}
                  )
                </VuiTypography>
              </VuiBox>

              {/* Locked column */}
              <VuiBox>
                <VuiTypography variant="caption" color="text" fontSize="xs">
                  Locked: {parseFloat(ethers.utils.formatUnits(marketTVL[1],childTokenDecimal === 0n ? 18 : childTokenDecimal)).toFixed(2)}
                </VuiTypography>
                <VuiTypography variant="caption" color="text" fontSize="xs" display="block">
                  (${" "}
                  {(
                    parseFloat(ethers.utils.formatUnits(marketTVL[1],childTokenDecimal === 0n ? 18 : childTokenDecimal)) * childMarket?.priceUsd || 0
                  ).toFixed(2)}
                  )
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>

          {/* Action button */}
          <Link
            to={action.route}
            style={{ textDecoration: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            <VuiButton
              fullWidth
              sx={{
                py: 2,
                px: 4,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                color: "white",
                fontWeight: "bold",
                textTransform: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.5)",
                },
              }}
            >
              {action.label}
            </VuiButton>
          </Link>

          {/* Expandable section */}
          <VuiBox mt={2}>
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={1}
              borderRadius="8px"
              sx={{
                background: "rgba(255, 255, 255, 0.05)",
                cursor: "pointer",
                transition: "background-color 0.2s",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.1)",
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                setExpanded(!expanded);
                e.stopPropagation();
              }}
            >
              <VuiTypography variant="caption" color="text">
                {expanded ? "Show less" : "Show more"}
              </VuiTypography>
              <Icon sx={{ fontSize: 16, ml: 1, color: "text" }}>
                {expanded ? "expand_less" : "expand_more"}
              </Icon>
            </VuiBox>

            {expanded && (
              <VuiBox
                mt={2}
                p={3}
                borderRadius="12px"
                display="flex"
                flexDirection="column"
                gap={2}
                sx={{ background: "rgba(255, 255, 255, 0.05)" }}
              >
                  {/* Market Status */}
                  <VuiBox>
                  <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                    Market Creator
                  </VuiTypography>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    borderRadius="8px"
                    sx={{
                      background: "rgba(255, 255, 255, 0.05)",
                      cursor: "pointer",
                      "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
                    }}
                  >
                    <VuiTypography variant="button" color="white">
                      {shortenAddress(creator)}
                    </VuiTypography>
                    <Icon sx={{ color: "white", fontSize: 16, ml: 1 }}>content_copy</Icon>
                  </VuiBox>
                </VuiBox>

                {/* Divider */}
                <VuiBox
                  sx={{ width: "100%", height: "1px", background: "rgba(255, 255, 255, 0.1)" }}
                />

                {/* Market Status */}
                <VuiBox>
                  <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                    Market Status
                  </VuiTypography>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    borderRadius="8px"
                    sx={{
                      background: "rgba(255, 255, 255, 0.05)",
                      cursor: "pointer",
                      "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
                    }}
                  >
                    <VuiTypography variant="button" color="white">
                      {status == 0 ? "Active" : status == 1 ? "Processing" : status==2? "Closed" : "Failed: Retry pending"}
                    </VuiTypography>
                    <Icon sx={{ color: "white", fontSize: 16, ml: 1 }}>content_copy</Icon>
                  </VuiBox>
                </VuiBox>

                {/* Divider */}
                <VuiBox
                  sx={{ width: "100%", height: "1px", background: "rgba(255, 255, 255, 0.1)" }}
                />

                {/* Mother Pair */}
                <VuiBox>
                  <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                    Mother Pair
                  </VuiTypography>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    borderRadius="8px"
                    sx={{
                      background: "rgba(255, 255, 255, 0.05)",
                      cursor: "pointer",
                      "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(motherPairAddress);
                    }}
                  >
                    <VuiTypography variant="button" color="white">
                      {shortenAddress(motherPairAddress)}
                    </VuiTypography>
                    <Icon sx={{ color: "white", fontSize: 16, ml: 1 }}>content_copy</Icon>
                  </VuiBox>
                </VuiBox>

                {/* Divider */}
                <VuiBox
                  sx={{ width: "100%", height: "1px", background: "rgba(255, 255, 255, 0.1)" }}
                />

                {/* Child Pair */}
                <VuiBox>
                  <VuiTypography variant="caption" color="text" mb={0.5} display="block">
                    Child Pair
                  </VuiTypography>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    borderRadius="8px"
                    sx={{
                      background: "rgba(255, 255, 255, 0.05)",
                      cursor: "pointer",
                      "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(childPairAddress);
                    }}
                  >
                    <VuiTypography variant="button" color="white">
                      {shortenAddress(childPairAddress)}
                    </VuiTypography>
                    <Icon sx={{ color: "white", fontSize: 16, ml: 1 }}>content_copy</Icon>
                  </VuiBox>
                </VuiBox>
              </VuiBox>
            )}
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </CardWrapper>
  );
}

export default MarketCard;
